import React from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_USER_PROFILE } from '../../graphql';
import { showInkToast } from './InkToast';
import { NewSiteModal } from './NewSiteModal';
import { SiteTile } from './SiteSwitcher';
import {
  planLabel,
  publishedLine,
  roleLabel,
  siteHue,
  SiteSummary,
  useTinycmsSites,
} from './sites';

const DAY = 86400000;

/**
 * All sites ("tinycms UX optimization review v6.zip", Prototype v3): every
 * site the user belongs to as a card, plus New site. ?new=1 opens New site
 * (the header's site menu links here that way).
 */
export const CmsSites: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { selectedAccount, selectAccountById } = useAccount();
  const { sites, loading } = useTinycmsSites();
  const { data: me } = useQuery<{ me?: { email?: string } }>(GET_USER_PROFILE);
  const creating = params.get('new') === '1';

  const openNew = () => setParams({ new: '1' });
  const closeNew = () => {
    params.delete('new');
    setParams(params, { replace: true });
  };

  const pick = (site: SiteSummary) => {
    if (site.accountId !== selectedAccount?.id) {
      selectAccountById(site.accountId);
      showInkToast(`Switched to ${site.name}.`);
    }
    navigate('/today');
  };

  return (
    <main className="ink-body sp">
      <div className="sp-head">
        <div>
          <h1 className="sp-title">Sites you manage</h1>
          {me?.me?.email ? <div className="sp-sub">{me.me.email}</div> : null}
        </div>
        <button type="button" className="ink-btn ink-btn-primary sp-new" onClick={openNew}>
          New site
        </button>
      </div>

      <div className="sp-grid">
        {sites.map((site) => {
          const here = site.accountId === selectedAccount?.id;
          const fresh = !here && Date.now() - new Date(site.createdAt).getTime() < DAY;
          const hue = siteHue(site.accountId);
          const tint = `oklch(0.95 0.03 ${hue})`;
          const meta = [
            roleLabel(site.role),
            planLabel(site.plan) &&
              `${planLabel(site.plan)}${site.status !== 'active' ? ` (${site.status})` : ''}`,
            site.ownerEmail ? `owned by ${site.ownerEmail}` : null,
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div
              key={site.accountId}
              className="sp-card"
              data-here={here ? 'true' : undefined}
              role="button"
              tabIndex={0}
              onClick={() => pick(site)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pick(site);
                }
              }}
            >
              <div className="sp-shot" style={{ background: tint }} aria-hidden="true">
                <div className="sp-shot-page">
                  <div className="sp-shot-name">{site.name}</div>
                  <div className="sp-shot-blocks">
                    <span style={{ background: tint }} />
                    <span style={{ background: tint }} />
                    <span style={{ background: tint }} />
                  </div>
                </div>
                {here ? <span className="sp-badge">Current</span> : null}
                {fresh ? <span className="sp-badge sp-badge-new">New</span> : null}
              </div>
              <div className="sp-info">
                <div className="sp-name-row">
                  <SiteTile site={site} size={22} />
                  <span className="sp-name">{site.name}</span>
                </div>
                {site.siteUrl ? (
                  <a
                    className="sp-host"
                    href={site.siteUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {site.host}
                  </a>
                ) : (
                  <span className="sp-host sp-host-none">No address yet</span>
                )}
                <div className="sp-meta">{meta}</div>
                <div className="sp-status" data-tone={site.waiting ? 'amber' : 'green'}>
                  <span className="sp-dot" />
                  {site.waiting ? `${site.waiting} waiting on you` : 'All caught up'}
                  <span className="sp-last">{publishedLine(site.lastPublishedAt)}</span>
                </div>
              </div>
            </div>
          );
        })}

        {!loading || sites.length ? (
          <button type="button" className="sp-add" onClick={openNew}>
            <span className="sp-add-plus">+</span>
            <span className="sp-add-title">New site</span>
            <span className="sp-add-sub">
              Start another site. Paid plans start billing the day you add it.
            </span>
          </button>
        ) : null}
      </div>

      {creating ? <NewSiteModal sites={sites} onClose={closeNew} /> : null}
    </main>
  );
};
